/**
 * Le decisioni del ricaricamento di riserva, senza timer e senza React.
 *
 * Il websocket resta il canale normale: quando è aperto gli aggiornamenti
 * arrivano dal server appena succedono, e non c'è niente da chiedere. Il
 * problema è il caso in cui **non si apre** — reti aziendali e proxy che non
 * lasciano passare l'aggiornamento a WebSocket, il caso che la configurazione
 * nginx prevede esplicitamente — o si blocca senza chiudersi. Lì il banco
 * smette di aggiornarsi e nessuno se ne accorge.
 *
 * Queste funzioni tengono la sola parte che si può sbagliare in silenzio: se
 * chiedere, e quando richiedere. I timer stanno in `useFallbackPoll`.
 */
import type { SocketStatus } from './useReconnectingSocket';

/**
 * Ogni quanto ricontrollare, quando si ricontrolla.
 *
 * Due secondi è il tempo massimo che si può aspettare per vedere una
 * restituzione fatta da un altro banco, e l'attesa qui non è tutta la storia:
 * quando la differenza c'è, il giro deve ancora chiedere la versione, poi lo
 * stato, e poi ridisegnare. Misurato su questa macchina, con un intervallo di
 * 1500 ms, il caso peggiore osservato è stato di 1732 ms: sotto i due secondi,
 * ma con un margine che su una rete mobile — dove due andate e ritorno costano
 * due o trecento millisecondi — non basta.
 *
 * 1200 ms lascia quindi circa mezzo secondo alle due richieste e al
 * ridisegno. Si paga un quarto di richieste in più, ma solo sui dispositivi
 * che hanno già perso il canale normale.
 */
export const FALLBACK_POLL_BASE_MS = 1_200;

/**
 * Quanta casualità aggiungere all'intervallo.
 *
 * Dieci tablet che ripartono insieme dopo un buco di rete chiederebbero tutti
 * nello stesso istante, con il server già occupato a servirli: sparpagliarli di
 * qualche centinaio di millisecondi costa niente e evita la fila. Con il
 * massimo a 1500 ms, il caso peggiore resta un quarto di secondo sotto il
 * limite dei due secondi.
 */
export const FALLBACK_POLL_JITTER_MS = 300;

export type FallbackPollConditions = {
  /** Stato del websocket: con `open` non c'è niente da chiedere. */
  status: SocketStatus;
  /** La schermata è montata e ha davvero qualcosa da aggiornare. */
  active: boolean;
  /** Rete e server raggiungibili: a rete assente non ha senso fallire ogni due secondi. */
  online: boolean;
  /** Scheda in primo piano: a scheda nascosta si risparmia il traffico. */
  visible: boolean;
};

/**
 * Vero solo se il ripiego serve davvero.
 *
 * La condizione sul websocket è la più importante: il ripiego è **di riserva**,
 * e finché il canale normale è aperto non deve aggiungere nemmeno una
 * richiesta. Le altre tre sono tutte necessarie, non alternative: basta che ne
 * manchi una perché il giro sia inutile o dannoso.
 */
export function shouldPoll(conditions: FallbackPollConditions): boolean {
  return (
    conditions.status !== 'open' &&
    conditions.active &&
    conditions.online &&
    conditions.visible
  );
}

/**
 * Quanto manca al prossimo giro, a partire da quando è **iniziato** quello
 * precedente.
 *
 * Misurare dall'inizio e non dalla fine è quello che tiene la cadenza fedele
 * anche quando il server è lento: con la misura dalla fine, una risposta da un
 * secondo porterebbe il giro a due secondi e mezzo, e il ritardo si
 * accumulerebbe. Mai negativo, così quando un giro dura più dell'intervallo il
 * successivo parte subito — senza però accavallarsi, perché il prossimo giro
 * si programma solo dopo che il precedente è finito.
 */
export function nextDelay(startedAt: number, now: number, intervalMs: number): number {
  return Math.max(0, startedAt + intervalMs - now);
}
